import React from 'react';
import { GdashActionType, ShiftGuardianSummaryResponse, inboundInboxService } from '../../services/inboundInboxService';

export type GDashCheckType = 'morning' | 'midday' | 'eod' | 'finish';

type Props = {
  open: boolean;
  checkType: GDashCheckType;
  summary: ShiftGuardianSummaryResponse | null;
  preview?: boolean;
  onClose: () => void;
  onOpenInbox?: (item?: ShiftGuardianSummaryResponse['items'][number]) => void | Promise<boolean | void>;
  onRemindLater?: () => void;
  onRefreshSummary?: (next?: ShiftGuardianSummaryResponse | null) => void;
};

const titleByType: Record<GDashCheckType, string> = {
  morning: 'Morning Inbox Briefing',
  midday: 'Midday Inbox Check',
  eod: 'End-of-Day Warning',
  finish: 'Shift Finish / Handover',
};

const subtitleByType: Record<GDashCheckType, string> = {
  morning: 'Carry-over and emails needing first attention',
  midday: 'Check overdue/no-reply/unassigned items',
  eod: 'Emails needing attention before leaving',
  finish: 'Final unresolved list and handover awareness',
};

const riskClass = (risk: string) => {
  if (risk === 'HIGH') return 'bg-rose-100 text-rose-700';
  if (risk === 'MEDIUM') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
};

const reasonLabel = (reason: string): string => {
  if (reason === 'UNASSIGNED') return 'Unassigned';
  if (reason === 'NO_REPLY_AFTER_INBOUND') return 'No Reply';
  if (reason === 'OVERDUE') return 'Overdue';
  return 'Needs Review';
};

const whyChipClass = (text: string): string => {
  const v = text.toLowerCase();
  if (v.includes('unassigned')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (v.includes('no reply')) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (v.includes('sla age')) return 'bg-sky-50 text-sky-700 border-sky-200';
  if (v.includes('open case')) return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const actionLabel = (action?: string | null): string => {
  if (action === 'OPEN') return 'Opened';
  if (action === 'ASSIGN_TO_ME') return 'Assigned to me';
  if (action === 'SET_FOLLOWUP_TOMORROW') return 'Follow-up set';
  if (action === 'MARK_SAFE_TOMORROW') return 'Marked safe';
  if (action === 'GDASH_OPENED_ITEM') return 'Opened';
  if (action === 'GDASH_ASSIGN_TO_ME') return 'Assigned to me';
  if (action === 'GDASH_FOLLOWUP_TOMORROW') return 'Follow-up set';
  if (action === 'GDASH_SAFE_UNTIL_TOMORROW') return 'Marked safe';
  return String(action || '');
};

const formatAge = (ageMinutes: number): string => {
  const minutes = Math.max(0, Math.floor(Number(ageMinutes) || 0));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 24 * 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  }
  if (minutes < 30 * 24 * 60) {
    const d = Math.floor(minutes / (24 * 60));
    const h = Math.floor((minutes % (24 * 60)) / 60);
    return `${d}d ${h}h`;
  }
  return 'Older than 30 days';
};

export const GDashPopup: React.FC<Props> = ({
  open,
  checkType,
  summary,
  preview = false,
  onClose,
  onOpenInbox,
  onRemindLater,
  onRefreshSummary,
}) => {
  const [openError, setOpenError] = React.useState<string | null>(null);
  const [openingKey, setOpeningKey] = React.useState<string>('');
  const [itemActionBusyKey, setItemActionBusyKey] = React.useState<string>('');
  const [itemActionErrorByKey, setItemActionErrorByKey] = React.useState<Record<string, string>>({});
  const [itemActionOverrideByKey, setItemActionOverrideByKey] = React.useState<Record<string, boolean>>({});
  const [itemActionOverrideTypeByKey, setItemActionOverrideTypeByKey] = React.useState<Record<string, GdashActionType>>({});
  const [minimized, setMinimized] = React.useState(false);
  const [dockPos, setDockPos] = React.useState<{ x: number; y: number } | null>(null);
  const openTimeoutRef = React.useRef<number | null>(null);
  const openingRef = React.useRef<{ messageId?: string; threadId?: string; openToken?: string } | null>(null);
  const dockRef = React.useRef<HTMLDivElement | null>(null);
  const dragRef = React.useRef<{
    active: boolean;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  }>({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  const clampDock = React.useCallback((x: number, y: number) => {
    const width = dockRef.current?.offsetWidth || 280;
    const height = dockRef.current?.offsetHeight || 56;
    const maxX = Math.max(8, window.innerWidth - width - 8);
    const maxY = Math.max(8, window.innerHeight - height - 8);
    return {
      x: Math.min(Math.max(8, x), maxX),
      y: Math.min(Math.max(8, y), maxY),
    };
  }, []);

  React.useEffect(() => {
    if (!minimized) return;
    const defaultWidth = dockRef.current?.offsetWidth || 280;
    const defaultHeight = dockRef.current?.offsetHeight || 56;
    const defaultPos = {
      x: Math.max(8, window.innerWidth - defaultWidth - 20),
      y: Math.max(8, window.innerHeight - defaultHeight - 120),
    };
    try {
      const raw = window.localStorage.getItem('gdash.dock.position');
      if (!raw) {
        setDockPos(clampDock(defaultPos.x, defaultPos.y));
        return;
      }
      const parsed = JSON.parse(raw);
      const px = Number(parsed?.x);
      const py = Number(parsed?.y);
      if (!Number.isFinite(px) || !Number.isFinite(py)) {
        setDockPos(clampDock(defaultPos.x, defaultPos.y));
        return;
      }
      setDockPos(clampDock(px, py));
    } catch {
      setDockPos(clampDock(defaultPos.x, defaultPos.y));
    }
  }, [clampDock, minimized]);

  React.useEffect(() => {
    if (!minimized) return;
    const onResize = () => {
      setDockPos((prev) => {
        if (!prev) return prev;
        const next = clampDock(prev.x, prev.y);
        try {
          window.localStorage.setItem('gdash.dock.position', JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampDock, minimized]);

  const onDockMouseDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (!dockPos) return;
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      originX: dockPos.x,
      originY: dockPos.y,
    };
    event.preventDefault();
  }, [dockPos]);

  React.useEffect(() => {
    if (!minimized) return;
    const onMove = (event: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag.active) return;
      const next = clampDock(
        drag.originX + (event.clientX - drag.startX),
        drag.originY + (event.clientY - drag.startY),
      );
      setDockPos(next);
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      if (dockPos) {
        try {
          window.localStorage.setItem('gdash.dock.position', JSON.stringify(dockPos));
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [clampDock, dockPos, minimized]);

  React.useEffect(() => {
    if (!open) return;
    const onOpened = (event: Event) => {
      const detail = (event as CustomEvent<{ messageId?: string; threadId?: string; openToken?: string }>).detail || {};
      const expected = openingRef.current;
      if (!expected) return;
      const sameToken = !!expected.openToken && !!detail.openToken && expected.openToken === detail.openToken;
      const sameMessage = !!expected.messageId && !!detail.messageId && expected.messageId === detail.messageId;
      const sameThread = !!expected.threadId && !!detail.threadId && expected.threadId === detail.threadId;
      if (!sameToken && !sameMessage && !sameThread) return;
      if (openTimeoutRef.current) {
        window.clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      setOpeningKey('');
      setOpenError(null);
      openingRef.current = null;
      setMinimized(true);
    };
    window.addEventListener('gdash:email-opened', onOpened as EventListener);
    return () => {
      window.removeEventListener('gdash:email-opened', onOpened as EventListener);
    };
  }, [onClose, open]);

  React.useEffect(() => () => {
    if (openTimeoutRef.current) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (open) return;
    if (openTimeoutRef.current) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    setOpeningKey('');
    setMinimized(false);
    openingRef.current = null;
  }, [open]);

  const clearOpenPendingState = React.useCallback(() => {
    if (openTimeoutRef.current) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    openingRef.current = null;
    setOpeningKey('');
    setOpenError(null);
  }, []);

  const handleOpenInbox = React.useCallback(async () => {
    clearOpenPendingState();
    setMinimized(false);
    try {
      const opened = await onOpenInbox?.();
      if (opened === false) {
        setOpenError('Could not open Inbox. Please navigate manually.');
        return;
      }
      onClose();
    } catch {
      setOpenError('Could not open Inbox. Please navigate manually.');
    }
  }, [clearOpenPendingState, onClose, onOpenInbox]);

  if (!open) return null;
  const s = summary?.summary || {
    totalNeedsAttention: 0,
    highRisk: 0,
    overdue: 0,
    unassigned: 0,
    noReplyAfterInbound: 0,
    mediumRisk: 0,
    lowRisk: 0,
  };
  const totalItems = summary?.items?.length || 0;
  const items = (summary?.items || [])
    .filter(item => {
      const hasId = !!String(item.emailMessageId || item.latestInboundMessageId || item.messageId || '').trim();
      const hasThread = !!String(item.threadId || '').trim();
      const hasMeaningfulContent =
        !!String(item.subject || '').trim() ||
        !!String(item.senderName || '').trim() ||
        !!String(item.senderEmail || '').trim() ||
        !!String(item.suggestedAction || '').trim();
      return hasId && hasThread && hasMeaningfulContent;
    })
    .slice(0, 5);
  const lookbackHours = Number(summary?.settings?.effectiveLookbackHours || summary?.settings?.guardianLookbackHours || 24);
  const subtitle = `Emails needing attention from the last ${lookbackHours} hours`;

  if (minimized) {
    return (
      <div
        ref={dockRef}
        className="fixed z-[12000] rounded-xl border border-rose-200 bg-white px-3 py-2 shadow-xl"
        style={{
          left: dockPos ? `${dockPos.x}px` : undefined,
          top: dockPos ? `${dockPos.y}px` : undefined,
          right: dockPos ? undefined : '20px',
          bottom: dockPos ? undefined : '120px',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Drag G.DASH dock"
            title="Drag"
            className="cursor-move rounded-md border border-slate-200 px-1.5 py-1 text-xs text-slate-500 hover:bg-slate-50"
            onMouseDown={onDockMouseDown}
          >
            ⋮⋮
          </button>
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-rose-400" />
          <div>
            <p className="text-xs font-semibold text-slate-700">{titleByType[checkType]}</p>
            <p className="text-[11px] text-slate-500">{items.length} items ready</p>
          </div>
          <button
            type="button"
            className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700"
            onClick={() => {
              clearOpenPendingState();
              setOpenError(null);
              setMinimized(false);
            }}
          >
            Reopen
          </button>
          <button
            type="button"
            className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white"
            onClick={onClose}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  const runItemAction = async (
    item: ShiftGuardianSummaryResponse['items'][number],
    action: GdashActionType,
    override?: boolean,
  ): Promise<boolean> => {
    const resolvedMessageId = String(item.emailMessageId || item.latestInboundMessageId || item.messageId || '').trim();
    const key = `${resolvedMessageId || ''}|${item.threadId || ''}`;
    setItemActionErrorByKey(prev => ({ ...prev, [key]: '' }));
    setItemActionOverrideByKey(prev => ({ ...prev, [key]: false }));
    setItemActionBusyKey(`${key}:${action}`);
    try {
      const result = await inboundInboxService.runGdashAction({
        messageId: resolvedMessageId || item.messageId,
        threadId: item.threadId,
        action,
        override: !!override,
      });
      if (!result.success) {
        const msg = result.message || 'Action failed';
        setItemActionErrorByKey(prev => ({ ...prev, [key]: msg }));
        return false;
      }
      if (action === 'OPEN') {
        if (import.meta.env.DEV) {
          console.debug(`[G.DASH Popup] requested open messageId=${resolvedMessageId || '<none>'}`);
        }
        const openToken = `gdash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setOpenError(null);
        setOpeningKey(key);
        openingRef.current = { messageId: resolvedMessageId, threadId: item.threadId, openToken };
        if (openTimeoutRef.current) window.clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = window.setTimeout(() => {
          if (!openingRef.current) return;
          setOpeningKey('');
          openingRef.current = null;
          setOpenError('Could not open this email. Try Open Inbox.');
        }, 15000);
        const opened = await onOpenInbox?.({ ...item, openToken } as any);
        if (opened === true) {
          if (openTimeoutRef.current) {
            window.clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
          }
          setOpeningKey('');
          openingRef.current = null;
          setOpenError(null);
          setMinimized(true);
          return true;
        }
        if (opened === false) {
          if (openTimeoutRef.current) {
            window.clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
          }
          setOpeningKey('');
          openingRef.current = null;
          setOpenError('Could not open this email. Try Open Inbox.');
          return false;
        }
      }
      const refreshed = await inboundInboxService.getShiftGuardianSummary({ checkType: preview ? 'preview' : checkType });
      onRefreshSummary?.(refreshed);
      return true;
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      const data = error?.response?.data || {};
      const code = String(data?.code || '');
      const msg = String(data?.message || data?.error || error?.message || 'Action failed');
      if (status === 409 && code === 'THREAD_LOCKED') {
        const lockName = String(data?.lockedByName || 'another user');
        setItemActionErrorByKey(prev => ({ ...prev, [key]: `This email is being handled by ${lockName}.` }));
        if (item.canCurrentUserOverride) {
          setItemActionOverrideByKey(prev => ({ ...prev, [key]: true }));
          setItemActionOverrideTypeByKey(prev => ({ ...prev, [key]: action }));
        }
      } else if (status === 409 && code === 'THREAD_CHANGED') {
        setItemActionErrorByKey(prev => ({ ...prev, [key]: 'This email changed. Refresh G.DASH.' }));
      } else if (status === 409 && code === 'ASSIGNED_TO_OTHER') {
        setItemActionErrorByKey(prev => ({ ...prev, [key]: 'This email is already assigned to another operator.' }));
      } else {
        setItemActionErrorByKey(prev => ({ ...prev, [key]: msg }));
      }
      return false;
    } finally {
      setItemActionBusyKey('');
    }
  };

  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/30 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-100 bg-white px-5 py-4">
          {preview ? <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-600">Preview only</p> : null}
          <h3 className="text-lg font-semibold text-slate-900">{titleByType[checkType]}</h3>
          <p className="text-sm text-slate-500">{subtitleByType[checkType]}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
            <div className="rounded-lg bg-slate-50 px-2 py-1"><span className="text-slate-500">Needs attention</span><div className="font-semibold text-slate-900">{s.totalNeedsAttention}</div></div>
            <div className="rounded-lg bg-slate-50 px-2 py-1"><span className="text-slate-500">High risk</span><div className="font-semibold text-slate-900">{s.highRisk}</div></div>
            <div className="rounded-lg bg-slate-50 px-2 py-1"><span className="text-slate-500">Overdue</span><div className="font-semibold text-slate-900">{s.overdue}</div></div>
            <div className="rounded-lg bg-slate-50 px-2 py-1"><span className="text-slate-500">Unassigned</span><div className="font-semibold text-slate-900">{s.unassigned}</div></div>
          </div>
          <p className="text-[11px] font-medium text-slate-500">Showing {items.length} of {totalItems} items</p>
          <div className="min-h-0 max-h-[45vh] space-y-1 overflow-y-auto pr-1">
            {items.length === 0 ? (
              <p className="text-sm text-slate-500">No items for this check.</p>
            ) : items.map(item => {
              const resolvedMessageId = String(item.emailMessageId || item.latestInboundMessageId || item.messageId || '').trim();
              const itemKey = `${resolvedMessageId || ''}|${item.threadId || ''}`;
              const lockByOther = !!item.lockState?.locked && !!item.lockState?.lockedByUserId && item.lockState.lockedByUserId !== null && !item.canCurrentUserAct;
              const actionLocked = !!lockByOther;
              const busyOpen = itemActionBusyKey === `${itemKey}:OPEN` || openingKey === itemKey;
              const busyAssign = itemActionBusyKey === `${itemKey}:ASSIGN_TO_ME`;
              const busyFollowup = itemActionBusyKey === `${itemKey}:SET_FOLLOWUP_TOMORROW`;
              const busySafe = itemActionBusyKey === `${itemKey}:MARK_SAFE_TOMORROW`;
              const anyBusy = busyOpen || busyAssign || busyFollowup || busySafe;
              const canAssign = !item.assignedToUserId && summary?.settings?.gdashActionsEnabled !== false && summary?.settings?.operatorCanAssignToSelf !== false;
              const canOwnerActions = summary?.settings?.gdashActionsEnabled !== false && (Boolean(item.assignedToUserId) ? true : canAssign);
              return (
              <div key={item.messageId} className="w-full rounded-lg border border-slate-100 px-2 py-2 text-left hover:bg-slate-50">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate text-sm font-medium text-slate-900">{item.subject}</p>
                  <div className="flex items-center gap-1">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${riskClass(item.riskLevel)}`}>{item.riskLevel}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{reasonLabel(item.reason)}</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">{item.senderName || item.senderEmail || 'Unknown sender'} · {formatAge(item.ageMinutes)}</p>
                {Array.isArray(item.whyShown) && item.whyShown.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {item.whyShown.map((why, idx) => (
                      <span key={`${itemKey}-why-${idx}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${whyChipClass(String(why || ''))}`}>
                        {String(why || '').replace(/^SLA age:\s*/i, 'SLA ')}
                      </span>
                    ))}
                  </div>
                ) : null}
                <p className="mt-1 text-[11px] font-semibold text-slate-500">Suggested next step</p>
                <p className="text-xs text-slate-700">{item.suggestedAction}</p>
                {item.lastGDashAction ? (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Last action: {actionLabel(item.lastGDashAction)} by {item.lastGDashActionByName || 'Unknown'}{item.lastGDashActionAt ? ` at ${new Date(item.lastGDashActionAt).toLocaleTimeString()}` : ''}
                  </p>
                ) : null}
                {lockByOther ? (
                  <p className="mt-1 text-[11px] font-medium text-amber-700">
                    Being handled by {item.lockState?.lockedByName || 'another user'}
                  </p>
                ) : null}
                <div className="mt-1 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => void runItemAction(item, 'OPEN')}
                    className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 disabled:opacity-60"
                    disabled={anyBusy || actionLocked}
                  >
                    {busyOpen ? 'Opening…' : 'Open'}
                  </button>
                  {canAssign ? (
                    <button
                      type="button"
                      onClick={() => void runItemAction(item, 'ASSIGN_TO_ME')}
                      className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 disabled:opacity-60"
                      disabled={anyBusy || actionLocked}
                    >
                      {busyAssign ? 'Assigning…' : 'Assign to me'}
                    </button>
                  ) : null}
                  {canOwnerActions ? (
                    <button
                      type="button"
                      onClick={() => void runItemAction(item, 'SET_FOLLOWUP_TOMORROW')}
                      className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 disabled:opacity-60"
                      disabled={anyBusy || actionLocked}
                    >
                      {busyFollowup ? 'Setting…' : 'Follow up tomorrow 9AM'}
                    </button>
                  ) : null}
                  {summary?.settings?.operatorCanMarkSafe || summary?.settings?.allowAdminOverride ? (
                    <button
                      type="button"
                      onClick={() => void runItemAction(item, 'MARK_SAFE_TOMORROW')}
                      className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 disabled:opacity-60"
                      disabled={anyBusy || actionLocked}
                    >
                      {busySafe ? 'Saving…' : 'Safe until tomorrow'}
                    </button>
                  ) : null}
                  {itemActionOverrideByKey[itemKey] ? (
                    <button
                      type="button"
                      onClick={() => void runItemAction(item, itemActionOverrideTypeByKey[itemKey] || 'OPEN', true)}
                      className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700"
                    >
                      Override and continue
                    </button>
                  ) : null}
                </div>
                {itemActionErrorByKey[itemKey] ? (
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-[11px] text-rose-600">{itemActionErrorByKey[itemKey]}</p>
                    {itemActionErrorByKey[itemKey].toLowerCase().includes('refresh') ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const refreshed = await inboundInboxService.getShiftGuardianSummary({ checkType: preview ? 'preview' : checkType });
                          onRefreshSummary?.(refreshed);
                        }}
                        className="rounded-md border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700"
                      >
                        Refresh
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )})}
          </div>
          {openError ? (
            <p className="text-xs text-rose-600">{openError}</p>
          ) : null}
        </div>
        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={() => {
              window.location.hash = '#/inbox/guardian';
              setMinimized(false);
              onClose();
            }}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700"
          >
            Open Guardian Queue
          </button>
          <button type="button" onClick={() => void handleOpenInbox()} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-60" disabled={preview && !onOpenInbox}>
            Open Inbox
          </button>
          <button type="button" onClick={onRemindLater} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700" disabled={preview}>
            Remind me in 15 minutes
          </button>
          <button type="button" onClick={onClose} className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};
